"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildDepTreeFromFiles = exports.buildDepGraphFromFiles = void 0;
const fs = require("fs");
const path = require("path");
const csProjParser = require("./parsers/csproj-parser");
const debugModule = require("debug");
const dotnetCoreParser = require("./parsers/dotnet-core-parser");
const dotnetCoreV2Parser = require("./parsers/dotnet-core-v2-parser");
const dotnetFrameworkParser = require("./parsers/dotnet-framework-parser");
const projectJsonParser = require("./parsers/project-json-parser");
const packagesConfigParser = require("./parsers/packages-config-parser");
const errors_1 = require("../errors");
const types_1 = require("./types");
const dotnet = require("./cli/dotnet");
const runtimeAssembly = require("./runtime-assembly");
const debug = debugModule('snyk');
const PARSERS = {
    'dotnet-core': {
        depParser: dotnetCoreParser,
        fileContentParser: JSON,
    },
    'dotnet-core-v2': {
        depParser: dotnetCoreV2Parser,
        fileContentParser: JSON,
    },
    'packages.config': {
        depParser: dotnetFrameworkParser,
        fileContentParser: packagesConfigParser,
    },
    'project.json': {
        depParser: dotnetFrameworkParser,
        fileContentParser: projectJsonParser,
    },
};
function getPackagesFolder(packagesFolder, projectRootFolder) {
    if (packagesFolder) {
        return path.resolve(process.cwd(), packagesFolder);
    }
    return path.resolve(projectRootFolder, 'packages');
}
function getRootName(root, projectRootFolder, projectNamePrefix) {
    const defaultRootName = path.basename(root || projectRootFolder || '');
    if (projectNamePrefix) {
        return projectNamePrefix + defaultRootName;
    }
    return defaultRootName;
}
function getFileContents(fileContentPath) {
    try {
        debug(`Parsing content of ${fileContentPath}`);
        return fs.readFileSync(fileContentPath, 'utf-8');
    }
    catch (error) {
        throw new errors_1.FileNotProcessableError(error);
    }
}
async function buildDepGraphFromFiles(root, targetFile, manifestType, useProjectNameFromAssetsFile, useRuntimeDependencies, projectNamePrefix) {
    var _a, _b;
    const safeRoot = root || '.';
    const safeTargetFile = targetFile || '.';
    const fileContentPath = path.resolve(safeRoot, safeTargetFile);
    const fileContent = getFileContents(fileContentPath);
    const projectRootFolder = path.resolve(fileContentPath, '../../');
    const targetFramework = await csProjParser.getTargetFrameworksFromProjFile(projectRootFolder);
    if (!targetFramework) {
        throw new errors_1.FileNotProcessableError(`unable to detect a target framework in ${projectRootFolder}, a valid one is needed to continue down this path.`);
    }
    const parser = PARSERS['dotnet-core-v2'];
    const manifest = await parser.fileContentParser.parse(fileContent);
    let resolvedProjectName = getRootName(root, projectRootFolder, projectNamePrefix);
    const projectNameFromManifestFile = (_b = (_a = manifest === null || manifest === void 0 ? void 0 : manifest.project) === null || _a === void 0 ? void 0 : _a.restore) === null || _b === void 0 ? void 0 : _b.projectName;
    if (manifestType === types_1.ManifestType.DOTNET_CORE &&
        useProjectNameFromAssetsFile) {
        if (projectNameFromManifestFile) {
            resolvedProjectName = projectNameFromManifestFile;
        }
        else {
            debug(`project.assets.json file doesn't contain a value for 'projectName'. Using default value: ${resolvedProjectName}`);
        }
    }
    let assemblyVersions = {};
    if (useRuntimeDependencies) {
        if (!runtimeAssembly.isSupported(targetFramework)) {
            throw new errors_1.FileNotProcessableError(`runtime resolution flag is currently only supported for: .NET versions 5 and higher, all versions of .NET Core and all versions of .NET Standard projects. Supplied versions was parsed as: ${targetFramework === null || targetFramework === void 0 ? void 0 : targetFramework.framework}.`);
        }
        // Ensure `dotnet` is installed on the system or fail trying.
        await dotnet.validate();
        // Run `dotnet publish` to create a self-contained publishable binary with included .dlls for assembly version inspection.
        const publishDir = await dotnet.publish(projectRootFolder);
        // Then inspect the dependency graph for the runtimepackage's assembly versions.
        const depsFile = path.resolve(publishDir, `${projectNameFromManifestFile}.deps.json`);
        assemblyVersions = runtimeAssembly.generateRuntimeAssemblies(depsFile);
    }
    const depGraph = parser.depParser.parse(resolvedProjectName, manifest, assemblyVersions);
    return {
        dependencyGraph: depGraph,
        targetFramework: targetFramework === null || targetFramework === void 0 ? void 0 : targetFramework.original,
    };
}
exports.buildDepGraphFromFiles = buildDepGraphFromFiles;
async function buildDepTreeFromFiles(root, targetFile, packagesFolderPath, manifestType, useProjectNameFromAssetsFile, projectNamePrefix) {
    var _a, _b;
    const safeRoot = root || '.';
    const safeTargetFile = targetFile || '.';
    const fileContentPath = path.resolve(safeRoot, safeTargetFile);
    const fileContent = getFileContents(fileContentPath);
    const projectRootFolder = path.resolve(fileContentPath, '../../');
    const packagesFolder = getPackagesFolder(packagesFolderPath, projectRootFolder);
    const tree = {
        dependencies: {},
        meta: {},
        name: getRootName(root, projectRootFolder, projectNamePrefix),
        packageFormatVersion: 'nuget:0.0.0',
        version: '0.0.0',
    };
    let targetFramework;
    try {
        if (manifestType === types_1.ManifestType.DOTNET_CORE) {
            targetFramework = await csProjParser.getTargetFrameworksFromProjFile(projectRootFolder);
        }
        else {
            // .csproj is in the same directory as packages.config or project.json
            const fileContentParentDirectory = path.resolve(fileContentPath, '../');
            targetFramework = await csProjParser.getTargetFrameworksFromProjFile(fileContentParentDirectory);
            // finally, for the .NETFramework project, try to assume the framework using dotnet-deps-parser
            if (!targetFramework) {
                // currently only process packages.config files
                if (manifestType === types_1.ManifestType.PACKAGES_CONFIG) {
                    targetFramework =
                        await packagesConfigParser.getMinimumTargetFramework(fileContent);
                }
            }
        }
    }
    catch (error) {
        return Promise.reject(error);
    }
    tree.meta = {
        targetFramework: targetFramework ? targetFramework.original : undefined, // TODO implement for more than one TF
    };
    const parser = PARSERS[manifestType];
    const manifest = await parser.fileContentParser.parse(fileContent, tree);
    if (manifestType === types_1.ManifestType.DOTNET_CORE &&
        useProjectNameFromAssetsFile) {
        const projectName = (_b = (_a = manifest === null || manifest === void 0 ? void 0 : manifest.project) === null || _a === void 0 ? void 0 : _a.restore) === null || _b === void 0 ? void 0 : _b.projectName;
        if (projectName) {
            tree.name = projectName;
        }
        else {
            debug("project.assets.json file doesn't contain a value for 'projectName'. Using default value: " +
                tree.name);
        }
    }
    return parser.depParser.parse(tree, manifest, targetFramework, packagesFolder);
}
exports.buildDepTreeFromFiles = buildDepTreeFromFiles;
//# sourceMappingURL=index.js.map